import { useMemo, useState, useSyncExternalStore } from 'react';
import { useImageOverlay } from '@/Pages/Clip/ClipPlayer/ClipAssets/ImageOverlay/useImageOverlay';
import MediaPickerModal from '@/Pages/BrandKit/components/MediaCard/MediaPickerModal';
import MediaPickerTabBar from '@/Pages/BrandKit/components/MediaCard/MediaPickerTabBar';
import MediaEmptyState from '@/Pages/BrandKit/components/MediaCard/MediaEmptyState';
import { useMediaPickerState } from '@/Pages/BrandKit/components/MediaCard/useMediaPickerState';
import { getMediaPickerConfirmDisabled } from '@/Pages/BrandKit/components/utils';
import { timelineActionsStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/timelineActions';
import FilesInput from '@/components/atoms/inputs/FilesInput/FilesInput';
import { PlatformBrandKitImage } from '@/Pages/BrandKit/types';
import { BrandKitMediaSection } from '@/Pages/BrandKit/components/MediaCard';
import { MEDIA_PICKER_TABS, BRANDKIT_IMAGE_TABS, type BrandKitImageTab } from '@/Pages/BrandKit/constants';

interface ImageOverlayModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ImageOverlayModal({ isOpen, onClose }: ImageOverlayModalProps) {
  const [selectedImage, setSelectedImage] = useState<PlatformBrandKitImage | undefined>();
  const [brandkitActiveTab, setBrandkitActiveTab] = useState<BrandKitImageTab>('logo');

  const { activeTab, setActiveTab, activeBrandKitId, setActiveBrandKitId, brandKits, activeKit, showUploadsOnly } =
    useMediaPickerState();

  const tabImages = useMemo(
    () =>
      brandkitActiveTab === 'logo'
        ? ((activeKit?.logos as PlatformBrandKitImage[]) ?? [])
        : ((activeKit?.photography_elements as PlatformBrandKitImage[]) ?? []),
    [brandkitActiveTab, activeKit]
  );

  const confirmDisabled = useMemo(
    () =>
      getMediaPickerConfirmDisabled({
        selectedUrl: selectedImage?.url,
        activeTab,
        brandKitMedia: tabImages
      }),
    [selectedImage?.url, activeTab, tabImages]
  );

  function handleBrandKitChange(brandKitId: string) {
    setActiveBrandKitId(brandKitId);
    setSelectedImage(undefined);
  }

  const { isAddingImage } = useSyncExternalStore(timelineActionsStore.subscribe, timelineActionsStore.getSnapshot);
  const { uploadImage, acceptedFormats, addBrandKitImage } = useImageOverlay({ onUploadComplete: onClose });

  function handleBrandkitTabChange(tab: BrandKitImageTab) {
    setBrandkitActiveTab(tab);
    setSelectedImage(undefined);
  }

  async function handleConfirm() {
    if (selectedImage) {
      await addBrandKitImage(selectedImage.url, selectedImage.originalName);
    }
    onClose();
  }

  const brandkitSubTabs = (
    <MediaPickerTabBar
      tabs={BRANDKIT_IMAGE_TABS}
      activeTabId={brandkitActiveTab}
      onTabChange={id => handleBrandkitTabChange(id as BrandKitImageTab)}
      variant="pill"
    />
  );

  return (
    <MediaPickerModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Image"
      onConfirm={handleConfirm}
      confirmDisabled={confirmDisabled}
      isConfirmLoading={isAddingImage}
    >
      {!showUploadsOnly && (
        <MediaPickerTabBar
          tabs={MEDIA_PICKER_TABS}
          activeTabId={activeTab}
          onTabChange={id => {
            setActiveTab(id as 'uploads' | 'brandkit');
            setSelectedImage(undefined);
          }}
        />
      )}

      {activeTab === 'uploads' && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-slate-700">Upload custom image</p>
            <FilesInput
              onFilesUpload={uploadImage}
              accept={acceptedFormats}
              multiple={false}
              icon="IconCloudUpload"
              title="Upload Image"
              label="Click to browse or drag and drop an image."
              rootClassNames="h-[155px]"
            />
          </div>
        </div>
      )}

      {activeTab === 'brandkit' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm font-medium text-slate-700">Brand Kit</p>
          <BrandKitMediaSection
            brandKits={brandKits}
            activeBrandKitId={activeBrandKitId}
            onBrandKitChange={handleBrandKitChange}
            images={tabImages}
            selectedUrl={selectedImage?.url}
            onSelect={setSelectedImage}
            imageEmptyState={
              <MediaEmptyState
                icon="IconPhoto"
                title={`No ${brandkitActiveTab === 'logo' ? 'logos' : 'photographs'} added`}
                description="Add images to your brand kit to reuse them across assets."
                ctaLabel="Add Images"
                ctaHref={activeBrandKitId ? `/brand-kit/${activeBrandKitId}/edit` : '/brand-kit'}
              />
            }
            selectorAddon={brandkitSubTabs}
          />
        </div>
      )}
    </MediaPickerModal>
  );
}
